import { apiClient } from './client';

export type WorkflowType = 'InternalAdvisor' | 'ExternalAdvisor' | 'Both';
export type TargetPlatform = 'APEX' | 'IFDS';
export type WorkflowStatus = 'Draft' | 'Pending' | 'Approved' | 'Rejected';

export type WorkflowStepType =
  | 'Welcome'
  | 'DynamicForm'
  | 'AdditionalDocuments'
  | 'PdfGeneration'
  /**
   * RETIRED 2026-08-08 — merged into 'PdfGeneration', which now owns the PDF
   * source templates as well as generating the filled output. The BE folds
   * legacy rows on read, so this should never arrive over the wire; kept in the
   * union so a stale bundle still type-checks against older data.
   */
  | 'PdfFileUpload'
  | 'OneSpanESign'
  | 'ApplicationReview';

export type WorkflowStep = {
  id: string;
  type: WorkflowStepType;
  titleEn: string;
  titleFr: string;
  descriptionEn: string;
  descriptionFr: string;
  /**
   * PDF source templates the originator uploaded. Only populated on the
   * 'PdfGeneration' step (was 'PdfFileUpload' before the 2026-08-08 merge).
   * Prefer {@link stepsWithPdfTemplates} over testing the step type — it keys
   * off the data, so it can't drift with the type rename.
   */
  fileEntries: FileEntry[];
  /**
   * Originator-defined document slots the advisor uploads at runtime. Only
   * meaningful when type === 'AdditionalDocuments'; empty for every other type.
   */
  additionalDocumentEntries: AdditionalDocumentEntry[];
  /**
   * File extensions (lowercase, no leading dot) accepted into the unnamed
   * "optional documents" bucket — a single advisor-facing dropzone, separate
   * from additionalDocumentEntries, that takes any number of files of these
   * types with no per-file name. Only meaningful when type ===
   * 'AdditionalDocuments'; empty for every other type.
   */
  optionalDocumentExtensions: string[];
  /**
   * Combined file-size cap (megabytes) for the optional-documents bucket.
   * Between {@link MIN_DOCUMENT_MAX_FILE_SIZE_MB} and {@link
   * MAX_DOCUMENT_MAX_FILE_SIZE_MB}. Only meaningful when type ===
   * 'AdditionalDocuments'.
   */
  optionalDocumentMaxFileSizeMb: number;
  /**
   * Whether the optional-documents bucket is shown to the advisor (and in the
   * originator's preview, which renders the same runtime step). Only
   * meaningful when type === 'AdditionalDocuments'; defaults to true.
   */
  showOptionalDocumentsSection: boolean;
  /**
   * Form builder canvas — sections containing rows of columns, each column holds
   * 0 or 1 field. Only populated when type === 'DynamicForm'; empty for every
   * other type. Section order, row order within a section, and within-row column
   * order are all canonical render orders at runtime.
   */
  sections: FormSection[];
  // Step-level visibility gating. Source fields live in PRIOR steps only —
  // scope enforced by the FE picker; the runtime evaluator decides at render
  // time and the BE refuses self-references. Empty list = always visible.
  conditionCombinator?: 'all' | 'any' | null;
  conditions?: FileEntryCondition[];
  /**
   * Originator-authored signing config — only meaningful on type === 'OneSpanESign'.
   * Null on every other type, and null on a fresh OneSpanESign step the originator
   * hasn't configured yet. See `OneSpanConfig` below for shape.
   */
  oneSpanConfig?: OneSpanConfig | null;
  /**
   * Landing-card content — only meaningful on type === 'Welcome'. Null until the
   * originator saves the config modal; the runtime falls back to i18n defaults +
   * live PDF entry labels while null.
   */
  welcomeConfig?: WelcomeConfig | null;
};

/**
 * Welcome step config — the landing card the advisor sees first. All text is
 * manually entered per language; `pdfListEn`/`pdfListFr` are free-text bullet
 * lines (prefilled in the modal from the workflow's PDF entries, then owned by
 * the originator). `showLanguageSelector` toggles the English/French radio that
 * gates the runtime language.
 */
export type WelcomeConfig = {
  titleEn: string;
  titleFr: string;
  subheaderEn: string;
  subheaderFr: string;
  descriptionEn: string;
  descriptionFr: string;
  showLanguageSelector: boolean;
  pdfListEn: string[];
  pdfListFr: string[];
};

/**
 * E-Signature step config. Drives the SIGNATORY payload BMO sends to OneSpan —
 * we never call OneSpan ourselves, so this is purely an authoring artifact:
 * which signers exist, what fields populate their contact info, and where each
 * signer signs inside each PDF.
 */
export type OneSpanConfig = {
  roles: SignatoryRole[];
  documents: SignatureDocument[];
};

export type SignatoryRole = {
  /** Lowercase slug the originator types; wire keys (`id_{slug}` / `r_{slug}`)
   *  are derived at payload-build time. */
  slug: string;
  type: SignatoryType;
  /** 1-based signing order. */
  index: number;
  /**
   * Required when `type === 'applicant'` (their info lives in the form);
   * must be null when `type === 'advisor'` (their info comes from the BFF
   * session at runtime).
   */
  fieldMappings: SignatoryFieldMappings | null;
  /**
   * Originator-typed display name shown wherever this signer is rendered
   * (e.g. the runtime preview tag). Required when `type === 'applicant'`;
   * null when `type === 'advisor'` (always shown as "Advisor").
   */
  labelEn: string | null;
  labelFr: string | null;
  // Gating conditions — does this signer get included in the signing
  // package. Same shape/semantics as step/section/field conditions. Empty
  // list = always included. Only meaningful for `type === 'applicant'`.
  conditionCombinator?: 'all' | 'any' | null;
  conditions?: FileEntryCondition[];
};

export type SignatoryType = 'applicant' | 'advisor';

export type SignatoryFieldMappings = {
  /** Required at submit time. May be Guid.Empty during draft state. */
  nameFieldId: string;
  /** Optional — many BMO forms collect a single "Full Name" field where last
   *  name lives inside the name string. Guid.Empty when not provided. */
  lastNameFieldId: string;
  emailFieldId: string;
  phoneFieldId: string;
};

export type SignatureDocument = {
  /** Reference to a `StepFileEntry.id` on a PdfFileUpload step in the same workflow. */
  pdfEntryId: string;
  fields: SignatureField[];
};

export type SignatureField = {
  /** Auto-incremented within a doc: `sig_field_1`, `sig_field_2`, … */
  name: string;
  /** Auto-incremented within a doc: `field_1`, `field_2`, … */
  binding: string;
  /** Literal string in the PDF — OneSpan anchors the signature widget by finding it. */
  textAnchor: string;
  /** Matches one of the parent config's role slugs. */
  role: string;
};

export type OneSpanConfigPayload = OneSpanConfig;

export type FormSection = {
  id: string;
  titleEn: string;
  titleFr: string;
  rows: FormRow[];
  // Visibility conditions on the section — when gated, every field inside
  // hides until the rule matches. Same shape as PDF/field conditions.
  conditionCombinator?: 'all' | 'any' | null;
  conditions: FileEntryCondition[];
  // §1 Repeatable Sections. When true, the advisor sees an "Add Another"
  // button below the section and can spawn multiple instances of it.
  // Each instance's values live under keys of the form
  // `inst:<sectionId>:<index>:<fieldId>` in the values map. Instance
  // count for a repeatable section is stored under
  // `__instanceCount:<sectionId>` (default 1 when unset).
  repeatable?: boolean;
  // Advisor-side floor on the number of instances. Always >= 1. The runtime
  // seeds this many starter instances and refuses to remove below it. Only
  // meaningful when `repeatable` is true; defaults to 1 when unset.
  minInstances?: number;
  // Advisor-side cap on the number of instances. Null = unlimited.
  maxInstances?: number | null;
  // Bilingual header pattern shown above each instance. Supports a `{n}`
  // placeholder that gets replaced with the 1-based instance index at
  // runtime ("Account Holder {n}" → "Account Holder 1", etc.). Null =
  // renderer falls back to "<titleEn> {n}" / "<titleFr> {n}".
  instanceLabelEn?: string | null;
  instanceLabelFr?: string | null;
};

export type FormRow = {
  id: string;
  columns: FormColumn[];
};

export type FormColumn = {
  id: string;
  /** Null = empty slot (the builder shows it as a drop target). */
  field: WorkflowField | null;
};

export type WorkflowFieldType =
  | 'ShortText'
  | 'LongText'
  | 'Number'
  | 'SingleSelect'
  | 'RadioGroup'
  | 'CheckboxGroup'
  // Boolean inputs — identical authoring shape, different runtime control.
  | 'Toggle'
  | 'Checkbox'
  // Layout blocks — non-input. The field's Label is the rendered content for
  // StaticText / Paragraph; Divider's Label is a canvas-only identifier.
  | 'StaticText'
  | 'Paragraph'
  | 'Divider'
  // Date input — runtime renders a calendar picker; authoring carries only the
  // standard Label + Required + Hint surface.
  | 'Date'
  // Layout block — renders Options as <ul>/<ol> at runtime. ListStyle property
  // chooses bullets vs. numbers.
  | 'BulletedList'
  // Layout block — tinted callout. Renders Label inside a colored box with
  // an optional leading icon. backgroundColor + icon are Notice-only.
  | 'Notice'
  // Repeatable rows — the originator authors a single row template (N column
  // specs); the advisor adds 1..N instances of that row at runtime via
  // "+ Add Another Row". Value persisted as an array of objects keyed by
  // each column's stable id.
  | 'RepeatableRow'
  // Layout block — static image with an optional bilingual caption. The image
  // is uploaded via the help-images endpoint (general-purpose for workflow-
  // scoped image uploads); the field stores only the URL.
  | 'Image';

/**
 * One column inside a {@link WorkflowField}'s repeatable row template.
 * Stable `id` keys each per-row value entry; the column's `type` picks
 * which scalar renderer + validator runs inside the row.
 */
export type RepeatableColumn = {
  id: string;
  type: WorkflowFieldType;
  labelEn: string;
  labelFr: string;
  required: boolean;
  placeholder?: string | null;
  maxLength?: number | null;
  options: SelectOption[];
  validate?: string | null;
  inputRestriction?: string | null;
  /** LEGACY single AcroForm handle (may carry a `{N}` token). Read-only; the
   *  mapping tool now writes per-row-index handles into {@link mappings}. */
  internalName?: string | null;
  /** Per-row-index AcroForm handles (1-based `rowIndex`), authored by the visual
   *  mapping tool. Each repeatable row maps independently. */
  mappings?: RepeatableColumnMapping[] | null;
  /** Per-column width as a percentage of the row (1–100). Columns share the row
   *  by these percentages; null/undefined = equal share. Values need not sum to
   *  100 — the runtime grid normalizes them proportionally. */
  widthPercent?: number | null;
  /** Bilingual help description shown as a tooltip beside the column name in the
   *  builder and at runtime. Null/blank = no help bubble. */
  helpEn?: string | null;
  helpFr?: string | null;
  /** Dual-unit money column — the advisor flips the WHOLE column between a
   *  dollar amount and a percentage from a control in the column header.
   *  Number columns only; null/undefined on every ordinary column. */
  unitToggle?: ColumnUnitToggle | null;
};

/**
 * Configuration for a dual-unit money column.
 *
 * The per-mode totals live here rather than on the field's `sumValidation`
 * because they are part of what the unit MEANS: RDSP needs dollars to total the
 * Contribution Amount while percentages total 100, and RIF needs percentages to
 * total 100 on two separate columns of one table. Either mode may carry NO
 * target — RIF leaves dollar mode unconstrained.
 */
export type ColumnUnitToggle = {
  /** Which unit the column starts in. */
  defaultMode: 'dollar' | 'percent';
  /** Header control label; falls back to "$/%" when absent. */
  labelEn?: string | null;
  labelFr?: string | null;
  dollarTargetFieldId?: string | null;
  dollarTargetConstant?: number | null;
  percentTargetFieldId?: string | null;
  percentTargetConstant?: number | null;
};

/** One AcroForm handle for a single (1-based) repeatable row of a column. */
export type RepeatableColumnMapping = { rowIndex: number; internalName: string };

/**
 * Reference to a prior-step field whose current value seeds another field
 * on step entry. Both ids are required; the BE refuses self-reference and
 * empty ids. See `WorkflowField.sourceField`.
 */
export type SourceFieldRef = {
  stepId: string;
  fieldId: string;
};

/**
 * Cross-row sum rule for a {@link WorkflowFieldType.RepeatableRow} field. See
 * `WorkflowField.sumValidation`. Exactly one of `targetFieldId` / `targetConstant`
 * is set — the domain refuses both or neither.
 */
export type RepeatableSumValidation = {
  /** Column id (matches a `RepeatableColumn.id`) whose values are summed. */
  columnKey: string;
  /** Field id whose current value is the target sum. Null when using a constant. */
  targetFieldId?: string | null;
  /** Absolute target sum. Null when using a target field. */
  targetConstant?: number | null;
  /** Optional bilingual caption shown next to the live total in place of the
   *  generic "Current sum" wording (e.g. "Total contributions"). */
  labelEn?: string | null;
  labelFr?: string | null;
};

/**
 * Layout-block types: non-input elements that render their own content (or
 * nothing, in Divider's case) instead of capturing data. Single source of
 * truth so the builder panel + runtime step-completion calc stay in sync —
 * adding a new layout-block type touches only this list.
 */
export const LAYOUT_BLOCK_TYPES: ReadonlySet<WorkflowFieldType> = new Set([
  'StaticText',
  'Paragraph',
  'Divider',
  'BulletedList',
  'Notice',
  'Image',
]);
export const isLayoutBlockType = (type: WorkflowFieldType): boolean => LAYOUT_BLOCK_TYPES.has(type);

/**
 * Baseline vertical breathing room (CSS px) the form renderer applies to
 * StaticText / Paragraph blocks when the originator hasn't set an explicit
 * marginTop / marginBottom. Mirrored in the right-panel slider initial value
 * so what the originator sees matches what runtime renders.
 */
export const DEFAULT_TEXT_MARGIN_PX = 16;

export type SelectOption = {
  value: string;
  // French-specific stored value. When set and the form language is FR, the
  // renderer uses this instead of `value` so province codes, answer tokens, etc.
  // can differ by language. Null means "same as value".
  valueFr?: string | null;
  labelEn: string;
  labelFr: string;
  // Optional per-item help bubble. Paired EN+FR — BE refuses an unpaired set.
  // Primarily surfaced on BulletedList items today (renders a "?" icon
  // inside the bullet); available on every options-type for future use.
  helpTitleEn?: string | null;
  helpTitleFr?: string | null;
  helpBodyEn?: string | null;
  helpBodyFr?: string | null;
  // CheckboxGroup-only — list of OTHER option values on the same group that
  // get unchecked at runtime when this option is checked. The BE refuses
  // this list on any other field type (RadioGroup / SingleSelect are
  // single-pick by construction; BulletedList isn't an input). Null/empty
  // means "no mutual-exclusion rule".
  clearsOnSelect?: string[] | null;
  // RadioGroup / CheckboxGroup only — id of another field on the SAME step
  // that this option borrows for inline display when selected. Null = no
  // borrow. The runtime hides the borrowed field from normal section flow and
  // renders it inline beneath the option label. The BE refuses two options
  // borrowing the same id within one group, and refuses the property outright
  // on SingleSelect (a dropdown) and BulletedList (not an input).
  revealFieldId?: string | null;
  // Per-option AcroForm checkbox mappings — Radio / SingleSelect / CheckboxGroup
  // only. When the advisor picks this option (or leaves it ticked for a
  // CheckboxGroup), the runtime CHECKs each mapped box at fill time. A list so
  // one option maps to many checkbox widgets across many PDFs (1-to-many).
  // Complements the field-level `pdfMappings` (text-slot fills). BE refuses on
  // other types.
  pdfMappings?: PdfFieldMapping[] | null;
  // Registry KEY (not a path) for the icon shown on this option's card —
  // resolved through `form-renderer/optionIcons.ts`. Only rendered when the
  // owning field sets `displayAs: 'card'`; a list-rendered group ignores it.
  // BE refuses on field types that can't lay options out as cards
  // (RadioGroup / CheckboxGroup are the only two). Null = no icon.
  icon?: string | null;
};

/**
 * One configurable input field on a DynamicForm step. Property keys are all-nullable
 * so the same shape carries every {@link WorkflowFieldType} — the BE rejects keys
 * that don't apply to the field's type at the domain layer.
 */

/**
 * One field-level PDF AcroForm mapping: the uploaded PDF entry it targets
 * (`pdfEntryId` = a PdfFileUpload step's fileEntry id) plus the AcroForm field
 * name. A field carries a list so it can populate many AcroForm fields across
 * many PDFs (1-to-many), including more than one field within the same PDF.
 * `pdfEntryId === EMPTY_GUID` is the legacy "applies to every document" sentinel
 * for mappings that predate per-entry scoping.
 */
export type PdfFieldMapping = {
  pdfEntryId: string;
  acroFieldName: string;
  /** 1-based instance of a repeatable section this mapping targets; null/absent
   *  for a non-repeatable field. */
  instanceIndex?: number | null;
};

/** The all-zero GUID the BE uses as the "applies to every document" sentinel
 *  on a legacy PDF mapping (rehydrated from the old scalar `internalName`). */
export const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

export type WorkflowField = {
  id: string;
  type: WorkflowFieldType;
  labelEn: string;
  labelFr: string;
  required: boolean;
  placeholder?: string | null;
  minLength?: number | null;
  maxLength?: number | null;
  min?: number | null;
  max?: number | null;
  step?: number | null;
  integerOnly?: boolean | null;
  options: SelectOption[];
  // Help bubble (per spec §6.3). Both halves are set together or both null.
  helpTitleEn?: string | null;
  helpTitleFr?: string | null;
  helpBodyEn?: string | null;
  helpBodyFr?: string | null;
  // Hint — short note shown under the input at runtime. EN+FR independent.
  hintEn?: string | null;
  hintFr?: string | null;
  // FontSize (CSS px) + Color (hex) — only meaningful on StaticText / Paragraph.
  fontSize?: number | null;
  color?: string | null;
  // MarginTop + MarginBottom (CSS px). Renderer defaults to 20 px when unset.
  marginTop?: number | null;
  marginBottom?: number | null;
  // ListStyle — only meaningful on BulletedList. 'disc' (default) or 'decimal'.
  listStyle?: string | null;
  // FontWeight — StaticText / Paragraph only. 'thin' | 'regular' | 'bold'.
  fontWeight?: string | null;
  // InputRestriction — ShortText / LongText only.
  // 'digits' | 'letters' | 'alphanumeric'. Runtime strips non-matching chars.
  inputRestriction?: string | null;
  // BackgroundColor + icon — Notice only. Renderer defaults to BMO blue +
  // info icon when unset on a Notice.
  backgroundColor?: string | null;
  icon?: string | null;
  // DisplayAs — Notice only. 'inline' (default) renders the callout in
  // document flow; 'modal' pops a modal dialog when the field's visibility
  // condition flips true.
  //
  // ALSO used by RadioGroup / CheckboxGroup with a separate, disjoint
  // vocabulary: 'list' (default) is the standard control-then-label stack,
  // 'card' renders each option as a bordered tile with an optional
  // per-option icon. The BE validates the two vocabularies independently, so
  // a Notice can't be 'card' and an option group can't be 'modal'.
  displayAs?: 'inline' | 'modal' | 'list' | 'card' | string | null;
  // BlockingValues — RadioGroup / SingleSelect / CheckboxGroup. When the
  // advisor's value for this field matches these option values, FormShell
  // disables Next on the step that owns this field. Lets the originator mark
  // an eligibility gate without copy-pasting visibility conditions onto every
  // later step.
  blockingValues?: string[] | null;
  // BlockingValueOperator — CheckboxGroup only. 'or' (default) blocks when the
  // selection contains any listed value; 'and' blocks only when it contains
  // all of them. RadioGroup / SingleSelect are always OR.
  blockingValueOperator?: 'and' | 'or' | null;
  // BlockContinueAlert — when true (and blockingValues is set), the renderer
  // pops an alert modal each time the advisor selects a blocking answer.
  blockContinueAlert?: boolean | null;
  // BlockContinueMessage — bilingual explanation for the blocking answer.
  // Shown as error text under the field, as the alert-modal body, and as
  // the disabled-Next tooltip (FR falls back to EN).
  blockContinueMessageEn?: string | null;
  blockContinueMessageFr?: string | null;
  // HideLabel — when true, FE renderer suppresses the field label above
  // the input. The label is still stored (BE requires it for identification),
  // it just doesn't render.
  hideLabel?: boolean | null;
  // VerticalOptions — RadioGroup / CheckboxGroup only. When true the renderer
  // stacks the options vertically (one per line) instead of the default
  // horizontal flow.
  verticalOptions?: boolean | null;
  // MaxRows — RepeatableRow only. Advisor-side cap on the number of rows;
  // null = unlimited. The "+ Add Another Row" button hides once reached.
  maxRows?: number | null;
  /** RepeatableRow only. True = the FIRST row can never be removed; the advisor
   *  deletes only rows they added. Independent of `displayAs` — a TABLE can
   *  anchor its first row, which card layout used to imply on its own.
   *  Null/undefined = fall back to the layout default. */
  anchorFirstRow?: boolean | null;
  // Validate — semantic rule the FE checks on blur and surfaces as an inline
  // error. sin/email/phone/postal are ShortText only; 'regex' is allowed on
  // ShortText + LongText.
  validate?: string | null;
  // RegexPattern — companion to validate === 'regex'. regexMessage — optional
  // bilingual override shown when a value fails the pattern.
  regexPattern?: string | null;
  regexMessageEn?: string | null;
  regexMessageFr?: string | null;
  // AddressAutocomplete — ShortText only. 'canada-post' swaps the plain
  // Input for an AntD AutoComplete backed by Canada Post's AddressComplete
  // API; on selection, the renderer fills sibling fields tagged with the
  // matching `addressRole` in the same section. Null = no autocomplete.
  addressAutocomplete?: 'canada-post' | null;
  // AddressRole — ShortText / SingleSelect only. Tags this field as one of
  // the slots an AddressComplete retrieve fills:
  //   - 'street' (the autocomplete-driving field itself)
  //   - 'city' / 'postal' / 'apt' (typically ShortText)
  //   - 'province' (typically SingleSelect with 2-letter-code values)
  // Sibling fill matches by role within the trigger field's section, so
  // renaming a label or running multiple Address compounds on one step
  // doesn't break the cross-field population. Null = not part of a
  // sibling group.
  addressRole?: 'street' | 'city' | 'province' | 'postal' | 'apt' | null;
  // MinAge — Date only. Whole years. Runtime disables dates that would make
  // the advisor younger than this; advisor + originator authoring share the
  // same property. Null = no min age.
  minAge?: number | null;
  // NumberFormat — Number only. 'currency' prepends "$" + thousands separators;
  // 'percentage' appends "%". Null = plain numeric input.
  numberFormat?: 'currency' | 'percentage' | string | null;
  // PdfMappings — field-level PDF AcroForm mappings. A field can map to many
  // AcroForm fields across many PDFs (1-to-many). Empty / omitted when unmapped;
  // refused on layout blocks at the BE.
  pdfMappings?: PdfFieldMapping[] | null;
  // RepeatableTemplate — column specs the runtime renders one row at a time
  // for RepeatableRow fields. Null on every other field type; refused at the
  // BE if stamped on something else.
  repeatableTemplate?: RepeatableColumn[] | null;
  // Cross-row sum rule — RepeatableRow only. Runtime sums the specified
  // Number column across every advisor-added row and requires the total
  // to equal either an absolute constant or the current value of another
  // field on the workflow. Null = no cross-row check.
  sumValidation?: RepeatableSumValidation | null;
  // Cross-field uniqueness tag — ShortText only. Every field tagged with
  // the same non-empty group name is treated as a peer set at runtime; a
  // duplicate value across peers blocks Next and paints the offender red.
  // Null / empty = "not in a group". Group names are originator-authored
  // strings; convention: `signatoryEmail`, `signatoryPhone`, etc.
  uniquenessGroup?: string | null;
  /** CheckboxGroup only. Advisor must tick at least this many options
   *  before the step completes. Null = plain `required` (any selection). */
  minSelections?: number | null;
  // Cross-step prefill reference. On step entry, if this field is empty
  // AND the advisor hasn't manually edited it, the runtime copies the
  // referenced field's current value into this slot. Advisor typing severs
  // the link. Refused on layout blocks and RepeatableRow at the BE. The
  // properties panel picker filters to PRIOR steps so the originator
  // can't author a same-step or later-step reference.
  sourceField?: SourceFieldRef | null;
  // ImageUrl — Image only. URL of the uploaded binary (lives in S3, served
  // via the help-images endpoint).
  imageUrl?: string | null;
  // ImageWidthSize — Image only. 'small' (33% of column), 'medium' (66%),
  // 'full' (100%). Renderer defaults to 'full' when unset.
  imageWidthSize?: 'small' | 'medium' | 'full' | string | null;
  // ImageAlignment — Image only. 'left' (default) / 'center' / 'right'.
  imageAlignment?: 'left' | 'center' | 'right' | string | null;
  // Caption — Image only. Bilingual caption rendered beneath the image.
  captionEn?: string | null;
  captionFr?: string | null;
  // Alt — Image only. Bilingual alt text on the rendered <img> for a11y.
  altEn?: string | null;
  altFr?: string | null;
  // Optional originator-authored override for the required-error message
  // shown at runtime when this field is `required` and the advisor leaves it
  // empty. Null/empty → renderer falls back to a generic message.
  requiredMessageEn?: string | null;
  requiredMessageFr?: string | null;
  // ReadOnly — when true, FE renderer disables the input and skips the
  // required-empty check for this field. Value is expected from prefill
  // (sourceValue) or a stored default. Layout blocks refuse this at the BE.
  readOnly?: boolean | null;
  // SourceValue — advisor-session prefill key. Domain allow-list today:
  // 'advisor.fullName' | 'advisor.email' | 'advisor.phone' |
  // 'advisor.dealerCode' | 'advisor.repCode'. FormShell resolves it via
  // useSession() when the step first mounts. Text-input types only.
  sourceValue?:
    | 'advisor.fullName'
    | 'advisor.email'
    | 'advisor.phone'
    | 'advisor.dealerCode'
    | 'advisor.repCode'
    | string
    | null;
  // Visibility conditions — gates whether this field renders at all.
  // Same shape as PDF/section conditions; runtime evaluation lands with the
  // advisor route. A field with conditions[] is not required-gated (the
  // step-completion calc treats it as optional until the evaluator lands).
  conditionCombinator?: 'all' | 'any' | null;
  conditions: FileEntryCondition[];
};

/**
 * Wire shape for adding/editing a field. The BE picks the relevant keys per
 * `type` and rejects the rest; the FE editor should only set keys that apply
 * to the field type the user is configuring.
 */
export type WorkflowFieldPropertiesPayload = {
  placeholder?: string | null;
  minLength?: number | null;
  maxLength?: number | null;
  min?: number | null;
  max?: number | null;
  step?: number | null;
  integerOnly?: boolean | null;
  options?: {
    value: string;
    valueFr?: string | null;
    labelEn: string;
    labelFr?: string | null;
    // Per-item help bubble. Paired (both halves or neither) — BE pair check.
    helpTitleEn?: string | null;
    helpTitleFr?: string | null;
    helpBodyEn?: string | null;
    helpBodyFr?: string | null;
    // CheckboxGroup mutual-exclusion targets — list of sibling option values
    // that get cleared at runtime when this option is checked. BE refuses
    // this on non-CheckboxGroup types.
    clearsOnSelect?: string[] | null;
    // RadioGroup / CheckboxGroup only — id of a sibling field on the same step
    // that this option borrows for inline display. BE refuses on other types
    // and refuses duplicates within one group.
    revealFieldId?: string | null;
    // Per-option AcroForm checkbox mappings (1-to-many across PDFs). Managed via
    // the option toggle endpoint; carried here so a full field edit round-trips
    // them instead of clearing them.
    pdfMappings?: PdfFieldMapping[] | null;
  }[];
  // Help bubble — both halves must be set together. BE refuses unpaired sets.
  helpTitleEn?: string | null;
  helpTitleFr?: string | null;
  helpBodyEn?: string | null;
  helpBodyFr?: string | null;
  // Hint — independent EN + FR optional strings.
  hintEn?: string | null;
  hintFr?: string | null;
  // FontSize (CSS px) + Color (hex). BE refuses on every type except
  // StaticText / Paragraph.
  fontSize?: number | null;
  color?: string | null;
  // MarginTop + MarginBottom (CSS px). Same scope.
  marginTop?: number | null;
  marginBottom?: number | null;
  // ListStyle — BulletedList only.
  listStyle?: string | null;
  // FontWeight — StaticText / Paragraph / Notice.
  fontWeight?: string | null;
  // InputRestriction — ShortText / LongText only.
  inputRestriction?: string | null;
  // BackgroundColor + icon — Notice only.
  backgroundColor?: string | null;
  icon?: string | null;
  // DisplayAs — Notice ('inline' default | 'modal') or the card-capable
  // option groups, RadioGroup / CheckboxGroup ('list' default | 'card').
  displayAs?: 'inline' | 'modal' | 'list' | 'card' | string | null;
  // BlockingValues — RadioGroup / SingleSelect / CheckboxGroup.
  blockingValues?: string[] | null;
  // BlockingValueOperator — CheckboxGroup only. 'or' (default) / 'and'.
  blockingValueOperator?: 'and' | 'or' | null;
  // BlockContinueAlert — alert modal on blocking answer. Requires blockingValues.
  blockContinueAlert?: boolean | null;
  // BlockContinueMessage — bilingual blocking explanation. Requires blockingValues.
  blockContinueMessageEn?: string | null;
  blockContinueMessageFr?: string | null;
  // HideLabel — suppress the field label in the renderer.
  hideLabel?: boolean | null;
  // VerticalOptions — RadioGroup / CheckboxGroup only. Stack options vertically.
  verticalOptions?: boolean | null;
  // MaxRows — RepeatableRow only.
  maxRows?: number | null;
  /** RepeatableRow only. True = the first row cannot be removed. */
  anchorFirstRow?: boolean | null;
  // Validate — sin/email/phone/postal (ShortText) or 'regex' (ShortText/LongText).
  validate?: string | null;
  // RegexPattern + bilingual regexMessage — companions to validate === 'regex'.
  regexPattern?: string | null;
  regexMessageEn?: string | null;
  regexMessageFr?: string | null;
  // AddressAutocomplete — ShortText only. 'canada-post' or null.
  addressAutocomplete?: 'canada-post' | null;
  // AddressRole — ShortText / SingleSelect only.
  addressRole?: 'street' | 'city' | 'province' | 'postal' | 'apt' | null;
  // MinAge — Date only.
  minAge?: number | null;
  // NumberFormat — Number only.
  numberFormat?: 'currency' | 'percentage' | string | null;
  // PdfMappings — field-level PDF AcroForm mappings (1-to-many). Managed mainly
  // via togglePdfMapping; carried here so a full field edit round-trips the
  // mappings instead of clearing them.
  pdfMappings?: PdfFieldMapping[] | null;
  // RepeatableTemplate — column specs for a RepeatableRow field. Refused
  // on every other type at the BE.
  repeatableTemplate?: RepeatableColumnPayload[] | null;
  // Image-only — URL of the uploaded binary + width / alignment presets.
  imageUrl?: string | null;
  imageWidthSize?: 'small' | 'medium' | 'full' | string | null;
  imageAlignment?: 'left' | 'center' | 'right' | string | null;
  // Image-only — bilingual caption + alt text.
  captionEn?: string | null;
  captionFr?: string | null;
  altEn?: string | null;
  altFr?: string | null;
  // Optional bilingual override for the required-error copy.
  requiredMessageEn?: string | null;
  requiredMessageFr?: string | null;
  // ReadOnly — disables input + skips required-empty check.
  readOnly?: boolean | null;
  // SourceValue — advisor-session prefill key. Domain allow-list.
  sourceValue?: string | null;
  // Cross-row sum rule — RepeatableRow only.
  sumValidation?: RepeatableSumValidation | null;
  // Cross-field uniqueness tag — ShortText only.
  uniquenessGroup?: string | null;
  /** CheckboxGroup only. Advisor must tick at least this many options
   *  before the step completes. Null = plain `required` (any selection). */
  minSelections?: number | null;
  // Cross-step prefill reference.
  sourceField?: SourceFieldRef | null;
};

/** Wire shape for one column inside a RepeatableRow's template. Mirrors the
 *  scalar field payload but only carries the keys relevant to the column's
 *  own scalar type. */
export type RepeatableColumnPayload = {
  id: string;
  type: WorkflowFieldType;
  labelEn: string;
  labelFr?: string | null;
  required: boolean;
  placeholder?: string | null;
  maxLength?: number | null;
  options?: {
    value: string;
    labelEn: string;
    labelFr?: string | null;
    helpTitleEn?: string | null;
    helpTitleFr?: string | null;
    helpBodyEn?: string | null;
    helpBodyFr?: string | null;
  }[];
  validate?: string | null;
  inputRestriction?: string | null;
  internalName?: string | null;
  widthPercent?: number | null;
  helpEn?: string | null;
  helpFr?: string | null;
};

/**
 * Editor lock state on a workflow. `null` when no live lock is held (or the existing
 * one has aged past the TTL — the BE drops expired locks from this payload). When
 * present, `heldByCaller` is the BE-computed flag for "is this me?" — render the
 * editor normally when true, render read-only with a "Currently being edited by
 * {name}" banner when false.
 *
 * Note: the holder's Entra OID intentionally never crosses the wire — the boolean
 * is computed server-side from the authenticated request, so the client never
 * sees a colleague's identifier. `holderName` is the display snapshot the BE
 * persisted at lock-acquire time.
 */
export type LockState = {
  heldByCaller: boolean;
  holderName?: string | null;
  lockedAt: string;
  expiresAt: string;
};

export type FileEntry = {
  id: string;
  label: string;
  enFileKey?: string | null;
  frFileKey?: string | null;
  // Per-language upload timestamps (ISO-8601 from the BE). The originator
  // drawer renders these as "Uploaded {date}" beneath each file row so the
  // approver / originator can see at a glance which version is in play.
  // Null when no file is attached on that side yet.
  enUploadedAt?: string | null;
  frUploadedAt?: string | null;
  // Gating conditions: ordered list + a shared combinator ("all" / "any").
  // Empty list + null combinator → "always include". Per-condition
  // combinators are intentionally not supported.
  conditionCombinator?: 'all' | 'any' | null;
  conditions: FileEntryCondition[];
  // Optional filename template with `{field:<internalName>}` and
  // `{today:<format>}` tokens the runtime substitutes at Preview /
  // Download time. Null = fall back to `${label}.pdf`.
  filenameTemplate?: string | null;
};

export type FileEntryCondition = {
  fieldStepId: string;
  fieldId: string;
  operator: string;
  /** Booleans pack the operator name here so the BE always sees a non-null string. */
  value: string;
  /**
   * Quantifier for a source field that lives inside a REPEATABLE section.
   * Absent = ordinary field, resolved by its bare id. `'any'` = satisfied
   * when at least one instance matches; `'all'` = satisfied only when every
   * instance matches (and never by zero instances).
   *
   * Orthogonal to `operator` on purpose — folding the quantifier into
   * operator names would double the list forever.
   */
  instanceScope?: 'any' | 'all' | null;
};

/**
 * One originator-defined document slot on an AdditionalDocuments step. Unlike
 * FileEntry, the originator never uploads a file here — the advisor supplies
 * it at runtime. allowedExtensions scopes which file types the advisor may
 * pick for this slot; doNotParseToPdf opts the slot out of the platform's
 * default non-PDF -> PDF conversion.
 */
export type AdditionalDocumentEntry = {
  id: string;
  label: string;
  allowedExtensions: string[];
  isRequired: boolean;
  doNotParseToPdf: boolean;
  /** Per-entry combined file-size cap (megabytes), between {@link
   *  MIN_DOCUMENT_MAX_FILE_SIZE_MB} and {@link MAX_DOCUMENT_MAX_FILE_SIZE_MB}.
   *  Enforced at upload time. */
  maxFileSizeMb: number;
  conditionCombinator?: 'all' | 'any' | null;
  conditions: FileEntryCondition[];
  /** Same tokens as `FileEntry.filenameTemplate`. Applied to the
   *  advisor's uploaded file when packaged into the Download-all zip;
   *  the original filename is kept when null. */
  filenameTemplate?: string | null;
};

/** Bounds for the free-form max-file-size input the originator drawer surfaces
 *  for every mandatory-doc entry AND for the step-level optional bucket.
 *  Mirrors the BE range check at `AdditionalDocumentEntry.MinMaxFileSizeMb` /
 *  `MaxMaxFileSizeMb`. */
export const MIN_DOCUMENT_MAX_FILE_SIZE_MB = 1;
export const MAX_DOCUMENT_MAX_FILE_SIZE_MB = 25;

/** Soft threshold above which the drawer warns the originator that large
 *  attachments can be slow to upload or bounce off email size limits — the
 *  value is still savable past this point. Mirrors BE
 *  `AdditionalDocumentEntry.FileSizeWarningThresholdMb`. */
export const DOCUMENT_MAX_FILE_SIZE_WARNING_THRESHOLD_MB = 6;

export type ReorderDirection = 'Up' | 'Down';

export type WorkflowTemplate = {
  id: string;
  appName: string;
  nameEn: string;
  nameFr: string;
  descriptionEn: string;
  descriptionFr: string;
  type: WorkflowType;
  platform: TargetPlatform;
  order: number;
  ownerOid: string;
  /** Display name of the creator at draft-creation time. Empty for legacy rows; FE falls back to ownerOid. */
  ownerName: string;
  status: WorkflowStatus;
  currentVersionNumber: number;
  /** Label of the active approved version (e.g. "v1.2"). Empty until first approval. */
  currentVersionLabel: string;
  /** Label of the in-flight Draft/Pending/Rejected row (e.g. "v3"). Empty when the workflow is sitting on its published version. */
  inProgressVersionLabel: string;
  isActive: boolean;
  hasUnsubmittedChanges: boolean;
  createdAt: string;
  updatedAt: string;
  /** Ordered list of workflow steps. Always returned (empty array when there are none). */
  steps: WorkflowStep[];
  /** Number of steps on the template. Populated on EVERY workflow response, including
   *  the list endpoint where `steps` itself is suppressed — see `WorkflowSummary`.
   *
   *  NULL means UNKNOWN, not zero: a row persisted before the count was denormalized,
   *  read without its step graph. Treat unknown as "allow" — the BE's EnsureSubmittable
   *  is authoritative and still refuses a genuinely empty workflow, so the user gets an
   *  error on click rather than a permanently dead button. Such rows heal on next save. */
  stepCount: number | null;
  /** Live editor-lock state — null when nobody is editing or the previous lock has expired. */
  lock?: LockState | null;
  /** Newly-approved version awaiting the Approver's explicit promote. Set when
   *  Approve fires on top of an already-live published version (controlled
   *  rollout — v1 keeps serving until the Approver toggles publish on v2).
   *  Null/0 when no staged approval exists. */
  pendingApprovedVersionNumber?: number | null;
  /** Display label paired with `pendingApprovedVersionNumber` (e.g. "v2"). Empty when none. */
  pendingApprovedVersionLabel?: string;
  /** Timestamp of the most recent submit-for-approval action. Null on Drafts that
   *  have never been submitted and on legacy rows written before this field landed —
   *  the WorkflowCard hides the "Submitted" row in that case. */
  submittedAt?: string | null;
  /** When an approver archived this workflow; null while active. Orthogonal to
   *  `status` — an archived workflow keeps the Approved/Rejected status it held,
   *  which is what the Archive page shows alongside it. */
  archivedAt?: string | null;
  /** True when `archivedAt` is set. Sent explicitly so the FE never infers
   *  archived-ness from a timestamp's presence. */
  isArchived?: boolean;
  /** Optional dealer-code allow-list. When non-empty, an advisor only sees this
   *  application type if their own dealer code appears here. Combined with
   *  `excludedDealerCodes` via AND when both are non-empty. */
  includedDealerCodes: string[];
  /** Optional dealer-code deny-list — the inverse of `includedDealerCodes`. */
  excludedDealerCodes: string[];
};

/**
 * What the list endpoint (`GET /workflows`) actually returns: every card field, but
 * NO `steps`. The step graph carries all fields, PDF entries and conditions on a
 * workflow and dwarfs the metadata the dashboards render, so the BE suppresses it
 * there and sends `stepCount` instead.
 *
 * This is a distinct type rather than `WorkflowTemplate` with an empty array on
 * purpose: omitting the key makes `w.steps` a COMPILE error on any list-fed view,
 * instead of a silently-empty array that reads as "this workflow has no steps".
 * Anything that genuinely needs steps must fetch the single template via
 * `workflowsApi.get(id)`.
 *
 * A full `WorkflowTemplate` remains assignable to `WorkflowSummary`, so write-through
 * responses from create / patch / cancel-pending can be patched straight into list state.
 */
export type WorkflowSummary = Omit<WorkflowTemplate, 'steps'>;

/**
 * One row in a Canada Post AddressComplete Find response. `next` tells
 * the FE whether picking this row drills further (call Find again with
 * the row's `id` as `lastId`) or completes (call Retrieve with the id).
 */
export type AddressSuggestion = {
  id: string;
  text: string;
  description: string;
  highlight: string;
  cursor: string;
  next: 'Find' | 'Retrieve' | string;
};

/** ZeroBounce-normalized status. `Invalid` is the only value that blocks
 *  Next per INFLOWS-188 AC; everything else is either "definitely fine"
 *  (Valid), "we can't tell" (CatchAll/Unknown), or "reachable but should
 *  not send" (SpamTrap/DoNotMail — surface as warning but don't block). */
export type EmailVerifyStatus =
  'Valid' | 'Invalid' | 'CatchAll' | 'SpamTrap' | 'DoNotMail' | 'Unknown';

/** Per-email verification result echoed by the /integrations/email/verify
 *  endpoint. `didYouMean` is the typo suggestion (e.g. `gmial.com` →
 *  `gmail.com`) — worth surfacing as a warning even on a Valid result. */
export type EmailVerifyResult = {
  email: string;
  status: EmailVerifyStatus;
  subStatus: string | null;
  didYouMean: string | null;
};

/** Full structured address returned by Retrieve. Field names mirror the
 *  BE response; the consumer maps onto sibling form fields. */
export type AddressDetails = {
  line1: string;
  line2: string;
  /** Structured unit / apartment number (e.g. "232" for the address
   *  "232-46 Glamis Green SW"). The BE strips the matching "{subBuilding}-"
   *  prefix from line1 so writing subBuilding to the apartment field and
   *  line1 to the street field doesn't duplicate the unit. */
  subBuilding: string;
  city: string;
  provinceCode: string;
  provinceName: string;
  postalCode: string;
  countryCode: string;
  countryName: string;
};

// Orphaned as of the Phase 9 audit work (see recentDecisions() below). Kept commented for a
// future recent-decisions view; the backend /workflows/recent-decisions still returns this shape.
// /** One row per Approved/Rejected version for the approver dashboard. */
// export type WorkflowDecision = {
//   templateId: string;
//   appName: string;
//   nameEn: string;
//   nameFr: string;
//   versionNumber: number;
//   majorVersion: number;
//   minorVersion: number;
//   /** "v{major}.{minor}" (or "v{major}" when minor == 0). Authoritative for display. */
//   versionLabel: string;
//   status: WorkflowStatus;
//   // Approve / Reject are stored separately. Only one side is populated per row,
//   // matching the row's terminal status — never both at once.
//   approvedBy?: string | null;
//   approvedByName?: string | null;
//   /** Approver email snapshotted at decision time. Preferred over the name on the
//    *  Audit History page; null on legacy rows decided before email capture. */
//   approvedByEmail?: string | null;
//   approvedAt?: string | null;
//   rejectedBy?: string | null;
//   rejectedByName?: string | null;
//   /** Rejecter email snapshotted at decision time. See {@link approvedByEmail}. */
//   rejectedByEmail?: string | null;
//   rejectedAt?: string | null;
//   rejectionComment?: string | null;
//   /** Whether THIS version is the currently-live published row. True → renders
//    *  as "Published"; false → "Approved" (matches the dashboard's Approved
//    *  Workflows tab). Always false for Rejected decisions. */
//   isActive: boolean;
// };

/**
 * One row in the Audit History viewer. Unlike {@link WorkflowDecision} this covers EVERY
 * state change (create / submit / reject / approve / clone / reorder / …), sourced from the
 * backend AuditEvents table rather than decided version rows.
 */
export type AuditLogEntry = {
  actorOid: string;
  actorRole: string;
  /** Dotted action name, e.g. "workflow.submitted" / "workflow.approved". */
  action: string;
  entityType: string;
  entityId: string;
  /** Human-friendly entity name (workflow app name / custom-element name) snapshotted at
   *  emit time. Null on legacy rows written before the field landed — the FE falls back
   *  to `entityId` in that case. */
  entityName?: string | null;
  before?: string | null;
  after?: string | null;
  /** ISO-8601 timestamp of when the action occurred. */
  at: string;
  /** Display name snapshotted from the caller's JWT at emit time (SFB-05 AC3).
   *  Null on legacy rows written before the field landed — the FE falls back
   *  to `actorOid` in that case. */
  actorName?: string | null;
  /** Email snapshotted from the caller's JWT. Same null semantics as name. */
  actorEmail?: string | null;
};

export type WorkflowVersion = {
  templateId: string;
  versionNumber: number;
  majorVersion: number;
  minorVersion: number;
  /** "v{major}.{minor}" (or "v{major}" when minor == 0). */
  versionLabel: string;
  status: WorkflowStatus;
  createdBy: string;
  createdAt: string;
  approvedBy?: string | null;
  approvedByName?: string | null;
  /** Approver's email at approval time. Snapshotted alongside {@link approvedByName};
   *  BMO uses it on the versions history page for the AC3 audit ("who published this"). */
  approvedByEmail?: string | null;
  approvedAt?: string | null;
  rejectedBy?: string | null;
  rejectedByName?: string | null;
  rejectedByEmail?: string | null;
  rejectedAt?: string | null;
  rejectionComment?: string | null;
  /** Person who first flipped this version live (SetActive true or PromotePendingApproval).
   *  Distinct from approvedBy — approval and publish are separate steps in the
   *  controlled-rollout model. Legacy rows written before the split will have null
   *  even when live; FE falls back to the approver's identity in that case. */
  publishedBy?: string | null;
  publishedByName?: string | null;
  publishedByEmail?: string | null;
  publishedAt?: string | null;
  isActive: boolean;
  nameEn: string;
  nameFr: string;
  descriptionEn: string;
  descriptionFr: string;
  type: WorkflowType;
  platform: TargetPlatform;
  order: number;
  /** Source label (e.g., "v1") when this draft was forked from a non-current archived version. Null/absent otherwise. */
  forkedFromLabel?: string | null;
  /** Frozen snapshot of this version's step list as it was at submit / approval
   *  time. Lets the approver / originator preview an archived version without
   *  seeing the template's current state leak in.
   *
   *  Only present on the SINGLE-version endpoint (`workflowsApi.getVersion`). The
   *  version list omits it — see {@link WorkflowVersionSummary}. */
  steps: WorkflowStep[];
};

/**
 * What `workflowsApi.listVersions()` returns: every version row's metadata, but NO
 * frozen step snapshot. Snapshots live in their own table on the BE, and a versions
 * list would otherwise carry every snapshot a workflow has ever had just to render a
 * table of labels, statuses and audit stamps.
 *
 * Distinct type rather than an empty array, for the same reason as {@link WorkflowSummary}:
 * `v.steps` on a list-fed view is a COMPILE error instead of a silently-empty list that
 * would render a version as having no content. Fetch the one version you need to render
 * via `workflowsApi.getVersion(id, versionNumber)`.
 */
export type WorkflowVersionSummary = Omit<WorkflowVersion, 'steps'>;

export type WorkflowFormData = Omit<
  WorkflowTemplate,
  | 'id'
  | 'ownerOid'
  | 'ownerName'
  | 'lock'
  | 'status'
  | 'currentVersionNumber'
  | 'currentVersionLabel'
  | 'inProgressVersionLabel'
  | 'isActive'
  | 'hasUnsubmittedChanges'
  | 'createdAt'
  | 'updatedAt'
  | 'nameFr'
  | 'descriptionEn'
  | 'descriptionFr'
  | 'steps'
  // Server-derived, like the version/lock fields above — never part of the
  // create/edit form payload.
  | 'stepCount'
> & {
  // English title is mandatory; French title + descriptions are optional (ADR-0016).
  nameFr?: string;
  descriptionEn?: string;
  descriptionFr?: string;
};

/**
 * Every step carrying originator-uploaded PDF source templates.
 *
 * Keys off the DATA rather than the step type on purpose. Those templates lived on
 * 'PdfFileUpload' until the 2026-08-08 merge moved them to 'PdfGeneration', and four
 * separate call sites were each testing the type by hand — the PDF picker, the
 * field-mapping viewer, the approver's read-only drawer, and `collectPdfDocs`, which
 * is what actually produces the advisor's filled PDFs. A missed one there is silent:
 * the workflow simply generates nothing.
 *
 * The domain only ever populates `fileEntries` on the PDF step, so this is exact,
 * and it survives both the rename and a stale bundle meeting a folded payload.
 */
export function stepsWithPdfTemplates(template: WorkflowTemplate): WorkflowStep[] {
  return (template.steps ?? []).filter((s) => (s.fileEntries?.length ?? 0) > 0);
}

export const workflowsApi = {
  /** Dashboard list. Returns metadata only — see {@link WorkflowSummary} for why
   *  `steps` is absent and what to call when you need it. */
  list: async (): Promise<WorkflowSummary[]> => {
    const res = await apiClient.get<WorkflowSummary[]>('/workflows');
    return res.data;
  },

  get: async (id: string): Promise<WorkflowTemplate> => {
    const res = await apiClient.get<WorkflowTemplate>(`/workflows/${id}`);
    return res.data;
  },

  create: async (data: WorkflowFormData): Promise<WorkflowTemplate> => {
    const res = await apiClient.post<WorkflowTemplate>('/workflows', data);
    return res.data;
  },

  update: async (
    id: string,
    data: Omit<WorkflowFormData, 'appName'>,
  ): Promise<WorkflowTemplate> => {
    const res = await apiClient.patch<WorkflowTemplate>(`/workflows/${id}`, data);
    return res.data;
  },

  /**
   * IMPORTANT: only fire this when the form state has actually changed since the last
   * persisted snapshot. The BE refreshes the workflow's editor lock on every save call,
   * so a timer-driven autosave (firing on an interval regardless of changes) would keep
   * an idle tab's lock alive forever and break the AC09 14h expiry rule. Dirty-flag gate
   * any caller before invoking.
   */
  autosave: async (
    id: string,
    data: Omit<WorkflowFormData, 'appName'>,
  ): Promise<WorkflowTemplate> => {
    const res = await apiClient.put<WorkflowTemplate>(`/workflows/${id}/autosave`, data);
    return res.data;
  },

  submit: async (id: string): Promise<WorkflowTemplate> => {
    const res = await apiClient.post<WorkflowTemplate>(`/workflows/${id}/submit`);
    return res.data;
  },

  clone: async (sourceId: string, newAppName: string): Promise<WorkflowTemplate> => {
    const res = await apiClient.post<WorkflowTemplate>(`/workflows/${sourceId}/clone`, {
      newAppName,
    });
    return res.data;
  },

  remove: async (id: string): Promise<void> => {
    await apiClient.delete(`/workflows/${id}`);
  },

  /** Archived workflows, newest-archived first. Readable by both roles. */
  listArchived: async (): Promise<WorkflowTemplate[]> => {
    const res = await apiClient.get<WorkflowTemplate[]>('/workflows/archived');
    return res.data;
  },

  /**
   * Retire a workflow: Approved, Rejected, or anything already published
   * (`currentVersionNumber > 0`) whatever the status of an in-flight new
   * version. Pending is refused. Approver-only on the BE.
   */
  archive: async (id: string): Promise<WorkflowTemplate> => {
    const res = await apiClient.post<WorkflowTemplate>(`/workflows/${id}/archive`);
    return res.data;
  },

  /** Restore an archived workflow. It comes back as a DRAFT, not to the
   *  status it held when archived. Approver-only on the BE. */
  unarchive: async (id: string): Promise<WorkflowTemplate> => {
    const res = await apiClient.post<WorkflowTemplate>(`/workflows/${id}/unarchive`);
    return res.data;
  },

  setActive: async (
    id: string,
    isActive: boolean,
    language?: string,
  ): Promise<WorkflowTemplate> => {
    // ?lang=<en|fr> drives the "your workflow is now live" SES email body
    // when the flip goes from inactive → active (AC6 on the Publish story).
    // Optional so unit tests / non-i18n contexts don't break.
    const qs = language ? `?lang=${encodeURIComponent(language)}` : '';
    const res = await apiClient.patch<WorkflowTemplate>(`/workflows/${id}/active${qs}`, {
      isActive,
    });
    return res.data;
  },

  /**
   * Persist a new display order for one or more Approved workflows. Used by the
   * Originator's drag-to-reorder on the Published tab — the FE sends every
   * visible Published card with its new 0..N position so the BE writes a
   * coherent run instead of stitching partial updates.
   */
  reorderPublished: async (items: ReadonlyArray<{ id: string; order: number }>): Promise<void> => {
    await apiClient.post('/workflows/reorder', { items });
  },

  /** Version history WITHOUT step snapshots — see {@link WorkflowVersionSummary}. */
  listVersions: async (id: string): Promise<WorkflowVersionSummary[]> => {
    const res = await apiClient.get<WorkflowVersionSummary[]>(`/workflows/${id}/versions`);
    return res.data;
  },

  /** One version WITH its frozen step snapshot. Use when rendering a version's content
   *  (preview, step config, approver diff) rather than just its row in the history. */
  getVersion: async (id: string, versionNumber: number): Promise<WorkflowVersion> => {
    const res = await apiClient.get<WorkflowVersion>(`/workflows/${id}/versions/${versionNumber}`);
    return res.data;
  },

  revert: async (id: string, versionNumber: number): Promise<WorkflowTemplate> => {
    const res = await apiClient.post<WorkflowTemplate>(
      `/workflows/${id}/versions/${versionNumber}/revert`,
    );
    return res.data;
  },

  activateVersion: async (id: string, versionNumber: number): Promise<WorkflowTemplate> => {
    const res = await apiClient.post<WorkflowTemplate>(
      `/workflows/${id}/versions/${versionNumber}/activate`,
    );
    return res.data;
  },

  /** Approver-only: promote the staged pending-approved version to live.
   *  Demotes the prior current version + clears the template's pending
   *  fields. Fires when the Approver toggles publish on the "Pending
   *  Publishing" card after a re-approval. `language` drives the
   *  "your workflow is now live" email body (AC6 on the Publish story). */
  promotePendingApproval: async (id: string, language?: string): Promise<WorkflowTemplate> => {
    const qs = language ? `?lang=${encodeURIComponent(language)}` : '';
    const res = await apiClient.post<WorkflowTemplate>(
      `/workflows/${id}/promote-pending-approval${qs}`,
    );
    return res.data;
  },

  startNewVersion: async (id: string, sourceVersionNumber?: number): Promise<WorkflowTemplate> => {
    // Always send a JSON body — the BE endpoint declares a StartNewVersionRequest? param,
    // and minimal API returns 415 when there's no Content-Type. The default-fork path sends
    // an explicit null so the shape is consistent either way.
    const body = { sourceVersionNumber: sourceVersionNumber ?? null };
    const res = await apiClient.post<WorkflowTemplate>(`/workflows/${id}/start-new-version`, body);
    return res.data;
  },

  cancelNewVersion: async (id: string): Promise<WorkflowTemplate> => {
    const res = await apiClient.post<WorkflowTemplate>(`/workflows/${id}/cancel-new-version`);
    return res.data;
  },

  /**
   * Withdraw a Pending submission before the approver decides. Originator-only,
   * owner-only. v1-pending → template returns to Draft; v2+ pending → template
   * returns to Approved with v1 left serving live. `language` drives the
   * cancellation email's single-language render (same shape as approve/reject).
   */
  cancelPending: async (id: string, language?: string): Promise<WorkflowTemplate> => {
    const qs = language ? `?lang=${encodeURIComponent(language)}` : '';
    const res = await apiClient.post<WorkflowTemplate>(`/workflows/${id}/cancel-pending${qs}`);
    return res.data;
  },

  // ----- Steps -----

  addStep: async (
    templateId: string,
    body: {
      type: WorkflowStepType;
      titleEn: string;
      titleFr?: string;
      descriptionEn?: string;
      descriptionFr?: string;
    },
  ): Promise<WorkflowStep> => {
    const res = await apiClient.post<WorkflowStep>(`/workflows/${templateId}/steps`, body);
    return res.data;
  },

  editStep: async (
    templateId: string,
    stepId: string,
    body: {
      titleEn: string;
      titleFr?: string;
      descriptionEn?: string;
      descriptionFr?: string;
    },
  ): Promise<WorkflowStep> => {
    const res = await apiClient.patch<WorkflowStep>(
      `/workflows/${templateId}/steps/${stepId}`,
      body,
    );
    return res.data;
  },

  deleteStep: async (templateId: string, stepId: string): Promise<void> => {
    await apiClient.delete(`/workflows/${templateId}/steps/${stepId}`);
  },

  reorderStep: async (
    templateId: string,
    stepId: string,
    direction: ReorderDirection,
  ): Promise<void> => {
    await apiClient.post(`/workflows/${templateId}/steps/${stepId}/reorder`, { direction });
  },

  // ----- PDF entries (PdfFileUpload step) -----

  addFileEntry: async (
    templateId: string,
    stepId: string,
    body: { label: string; enFileKey?: string | null; frFileKey?: string | null },
  ): Promise<FileEntry> => {
    const res = await apiClient.post<FileEntry>(
      `/workflows/${templateId}/steps/${stepId}/file-entries`,
      body,
    );
    return res.data;
  },

  editFileEntry: async (
    templateId: string,
    stepId: string,
    entryId: string,
    body: { label: string; enFileKey?: string | null; frFileKey?: string | null },
  ): Promise<FileEntry> => {
    const res = await apiClient.patch<FileEntry>(
      `/workflows/${templateId}/steps/${stepId}/file-entries/${entryId}`,
      body,
    );
    return res.data;
  },

  deleteFileEntry: async (templateId: string, stepId: string, entryId: string): Promise<void> => {
    await apiClient.delete(`/workflows/${templateId}/steps/${stepId}/file-entries/${entryId}`);
  },

  /**
   * Replace the gating conditions on a PDF entry. Pass an empty list with a
   * null combinator to clear ("always include"); pass a non-empty list with
   * "all" or "any" to apply gating. The BE persists the list as-is and
   * evaluates it at PDF-generation time.
   */
  setFileEntryConditions: async (
    templateId: string,
    stepId: string,
    entryId: string,
    body: {
      conditions: FileEntryCondition[];
      combinator: 'all' | 'any' | null;
    },
  ): Promise<FileEntry> => {
    const res = await apiClient.put<FileEntry>(
      `/workflows/${templateId}/steps/${stepId}/file-entries/${entryId}/conditions`,
      body,
    );
    return res.data;
  },

  /** Replace the field's visibility conditions. Same wire shape as the PDF
   *  endpoint. Empty list + null combinator clears the gating. */
  setFieldConditions: async (
    templateId: string,
    stepId: string,
    sectionId: string,
    rowId: string,
    columnId: string,
    body: {
      conditions: FileEntryCondition[];
      combinator: 'all' | 'any' | null;
    },
  ): Promise<WorkflowField> => {
    const res = await apiClient.put<WorkflowField>(
      `/workflows/${templateId}/steps/${stepId}/sections/${sectionId}/rows/${rowId}/columns/${columnId}/field/conditions`,
      body,
    );
    return res.data;
  },

  /** Replace the section's visibility conditions. */
  setSectionConditions: async (
    templateId: string,
    stepId: string,
    sectionId: string,
    body: {
      conditions: FileEntryCondition[];
      combinator: 'all' | 'any' | null;
    },
  ): Promise<FormSection> => {
    const res = await apiClient.put<FormSection>(
      `/workflows/${templateId}/steps/${stepId}/sections/${sectionId}/conditions`,
      body,
    );
    return res.data;
  },

  /** Replace the step's visibility conditions. Source fields must live in
   *  PRIOR steps — scope enforced by the picker; the BE refuses self-reference. */
  setStepConditions: async (
    templateId: string,
    stepId: string,
    body: {
      conditions: FileEntryCondition[];
      combinator: 'all' | 'any' | null;
    },
  ): Promise<WorkflowStep> => {
    const res = await apiClient.put<WorkflowStep>(
      `/workflows/${templateId}/steps/${stepId}/conditions`,
      body,
    );
    return res.data;
  },

  // ----- DynamicForm builder canvas (sections → rows → columns → fields) -----

  addFormSection: async (
    templateId: string,
    stepId: string,
    payload: { titleEn: string; titleFr?: string | null },
  ): Promise<FormSection> => {
    const res = await apiClient.post<FormSection>(
      `/workflows/${templateId}/steps/${stepId}/sections`,
      payload,
    );
    return res.data;
  },

  editFormSection: async (
    templateId: string,
    stepId: string,
    sectionId: string,
    payload: { titleEn: string; titleFr?: string | null },
  ): Promise<FormSection> => {
    const res = await apiClient.patch<FormSection>(
      `/workflows/${templateId}/steps/${stepId}/sections/${sectionId}`,
      payload,
    );
    return res.data;
  },

  deleteFormSection: async (
    templateId: string,
    stepId: string,
    sectionId: string,
  ): Promise<void> => {
    await apiClient.delete(`/workflows/${templateId}/steps/${stepId}/sections/${sectionId}`);
  },

  setSectionRepeatable: async (
    templateId: string,
    stepId: string,
    sectionId: string,
    payload: {
      repeatable: boolean;
      minInstances?: number;
      maxInstances?: number | null;
      instanceLabelEn?: string | null;
      instanceLabelFr?: string | null;
    },
  ): Promise<FormSection> => {
    const res = await apiClient.put<FormSection>(
      `/workflows/${templateId}/steps/${stepId}/sections/${sectionId}/repeatable`,
      payload,
    );
    return res.data;
  },

  reorderFormSection: async (
    templateId: string,
    stepId: string,
    sectionId: string,
    direction: 'Up' | 'Down',
  ): Promise<void> => {
    await apiClient.post(`/workflows/${templateId}/steps/${stepId}/sections/${sectionId}/reorder`, {
      direction,
    });
  },

  /** Places a fresh, independent copy of a saved Section custom element into this step (appended at the end). */
  placeCustomSection: async (
    templateId: string,
    stepId: string,
    customElementId: string,
  ): Promise<FormSection> => {
    const res = await apiClient.post<FormSection>(
      `/workflows/${templateId}/steps/${stepId}/sections/place-custom/${customElementId}`,
    );
    return res.data;
  },

  /** Creates a new DynamicForm step pre-populated from a saved Step custom element (fresh ids, appended at the end). */
  placeCustomStep: async (templateId: string, customElementId: string): Promise<WorkflowStep> => {
    const res = await apiClient.post<WorkflowStep>(
      `/workflows/${templateId}/steps/place-custom-step/${customElementId}`,
    );
    return res.data;
  },

  addFormRow: async (
    templateId: string,
    stepId: string,
    sectionId: string,
    columnCount: number,
  ): Promise<FormRow> => {
    const res = await apiClient.post<FormRow>(
      `/workflows/${templateId}/steps/${stepId}/sections/${sectionId}/rows`,
      { columnCount },
    );
    return res.data;
  },

  deleteFormRow: async (
    templateId: string,
    stepId: string,
    sectionId: string,
    rowId: string,
  ): Promise<void> => {
    await apiClient.delete(
      `/workflows/${templateId}/steps/${stepId}/sections/${sectionId}/rows/${rowId}`,
    );
  },

  reorderFormRow: async (
    templateId: string,
    stepId: string,
    sectionId: string,
    rowId: string,
    direction: 'Up' | 'Down',
  ): Promise<void> => {
    await apiClient.post(
      `/workflows/${templateId}/steps/${stepId}/sections/${sectionId}/rows/${rowId}/reorder`,
      { direction },
    );
  },

  addFormColumn: async (
    templateId: string,
    stepId: string,
    sectionId: string,
    rowId: string,
  ): Promise<FormColumn> => {
    const res = await apiClient.post<FormColumn>(
      `/workflows/${templateId}/steps/${stepId}/sections/${sectionId}/rows/${rowId}/columns`,
    );
    return res.data;
  },

  removeFormColumn: async (
    templateId: string,
    stepId: string,
    sectionId: string,
    rowId: string,
    columnId: string,
  ): Promise<void> => {
    await apiClient.delete(
      `/workflows/${templateId}/steps/${stepId}/sections/${sectionId}/rows/${rowId}/columns/${columnId}`,
    );
  },

  placeField: async (
    templateId: string,
    stepId: string,
    sectionId: string,
    rowId: string,
    columnId: string,
    payload: {
      type: WorkflowFieldType;
      labelEn: string;
      labelFr?: string | null;
      required?: boolean;
      properties?: WorkflowFieldPropertiesPayload;
    },
  ): Promise<WorkflowField> => {
    const res = await apiClient.post<WorkflowField>(
      `/workflows/${templateId}/steps/${stepId}/sections/${sectionId}/rows/${rowId}/columns/${columnId}/field`,
      payload,
    );
    return res.data;
  },

  editField: async (
    templateId: string,
    stepId: string,
    sectionId: string,
    rowId: string,
    columnId: string,
    payload: {
      labelEn: string;
      labelFr?: string | null;
      required?: boolean;
      properties?: WorkflowFieldPropertiesPayload;
    },
  ): Promise<WorkflowField> => {
    const res = await apiClient.patch<WorkflowField>(
      `/workflows/${templateId}/steps/${stepId}/sections/${sectionId}/rows/${rowId}/columns/${columnId}/field`,
      payload,
    );
    return res.data;
  },

  // Set (or clear) a single repeatable column's PDF AcroForm mapping handle —
  // used by the visual PDF-mapping page. Repeatable columns keep the scalar 1:1
  // mapping; scalar fields use togglePdfMapping instead. Unlike editField (a
  // wholesale property replace), this leaves the rest of the field untouched.
  // Null/blank internalName clears the column's mapping.
  setFieldInternalName: async (
    templateId: string,
    stepId: string,
    sectionId: string,
    rowId: string,
    columnId: string,
    body: { internalName: string | null; repeatableColumnId: string; rowIndex: number },
  ): Promise<WorkflowField> => {
    const res = await apiClient.patch<WorkflowField>(
      `/workflows/${templateId}/steps/${stepId}/sections/${sectionId}/rows/${rowId}/columns/${columnId}/field/internal-name`,
      body,
    );
    return res.data;
  },

  // Toggle one field-level PDF AcroForm mapping (document entry + AcroForm field
  // name) on a scalar field — used by the visual PDF-mapping page. Adds the pair
  // when absent, removes it when present (click a box to map, click again to
  // unmap). Leaves the field's other properties untouched.
  togglePdfMapping: async (
    templateId: string,
    stepId: string,
    sectionId: string,
    rowId: string,
    columnId: string,
    body: { pdfEntryId: string; acroFieldName: string; instanceIndex?: number | null },
  ): Promise<WorkflowField> => {
    const res = await apiClient.patch<WorkflowField>(
      `/workflows/${templateId}/steps/${stepId}/sections/${sectionId}/rows/${rowId}/columns/${columnId}/field/pdf-mapping`,
      body,
    );
    return res.data;
  },

  // Toggle one per-option PDF AcroForm checkbox mapping on a choice field, keyed
  // by the option value — used by the visual PDF-mapping page. Adds the pair
  // when absent, removes it when present.
  toggleOptionPdfMapping: async (
    templateId: string,
    stepId: string,
    sectionId: string,
    rowId: string,
    columnId: string,
    body: {
      optionValue: string;
      pdfEntryId: string;
      acroFieldName: string;
      instanceIndex?: number | null;
    },
  ): Promise<WorkflowField> => {
    const res = await apiClient.patch<WorkflowField>(
      `/workflows/${templateId}/steps/${stepId}/sections/${sectionId}/rows/${rowId}/columns/${columnId}/field/option-pdf-mapping`,
      body,
    );
    return res.data;
  },

  removeField: async (
    templateId: string,
    stepId: string,
    sectionId: string,
    rowId: string,
    columnId: string,
  ): Promise<void> => {
    await apiClient.delete(
      `/workflows/${templateId}/steps/${stepId}/sections/${sectionId}/rows/${rowId}/columns/${columnId}/field`,
    );
  },

  moveField: async (
    templateId: string,
    stepId: string,
    payload: {
      fromSectionId: string;
      fromRowId: string;
      fromColumnId: string;
      toSectionId: string;
      toRowId: string;
      toColumnId: string;
    },
  ): Promise<void> => {
    await apiClient.post(`/workflows/${templateId}/steps/${stepId}/move-field`, payload);
  },

  /**
   * Single-shot insert of a multi-field compound (Address etc.) into a section.
   * Replaces what was previously a loop of N addFormRow + N×M placeField calls
   * — collapses to one BE round trip and one MutateUnderLockAsync cycle.
   */
  /**
   * Upload one help-bubble image (used inside a markdown body). Streams the file
   * through the API; the server stores it under `workflows/{id}/help-images/...`
   * and returns a stable, non-expiring URL the originator pastes into the body.
   */
  uploadHelpImage: async (
    templateId: string,
    file: File,
  ): Promise<{ id: string; filename: string; url: string; contentType: string }> => {
    const form = new FormData();
    form.append('file', file);
    const res = await apiClient.post<{
      id: string;
      filename: string;
      url: string;
      contentType: string;
    }>(`/workflows/${templateId}/help-images`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  },

  /**
   * Canada Post AddressComplete search. Returns a list of suggestions; the
   * caller drills via `lastId` when picking a row whose `next === 'Find'`,
   * or fires `addressLookupRetrieve` when picking a row with `next ===
   * 'Retrieve'`.
   */
  addressLookupFind: async (
    query: string,
    country: string = 'CAN',
    lastId?: string | null,
  ): Promise<AddressSuggestion[]> => {
    const params = new URLSearchParams({ q: query, country });
    if (lastId) params.set('lastId', lastId);
    const res = await apiClient.get<AddressSuggestion[]>(
      `/integrations/address/find?${params.toString()}`,
    );
    return res.data ?? [];
  },

  /**
   * Resolve a leaf suggestion id (from {@link addressLookupFind}) into a
   * structured address. The FE consumer maps fields onto sibling form
   * inputs (Address line / City / Province / Postal Code).
   */
  addressLookupRetrieve: async (id: string): Promise<AddressDetails | null> => {
    try {
      const res = await apiClient.get<AddressDetails>(
        `/integrations/address/retrieve?id=${encodeURIComponent(id)}`,
      );
      return res.data ?? null;
    } catch {
      return null;
    }
  },

  /**
   * ZeroBounce batch email verification (INFLOWS-188). Fires when the
   * advisor lands on the OneSpan E-signature step — every signatory email
   * on that step goes up in one call. Returns results in the same order
   * as the request. Any `Invalid` result blocks Next in the runtime.
   *
   * On any transport/HTTP failure, returns Unknown for every input so
   * a ZeroBounce outage or lost network doesn't strand the advisor mid-flow.
   * A console.error breadcrumb goes out so on-call has something to chase
   * when a support ticket says "the block never fires" — the FE catch
   * would otherwise silently swallow every 4xx/5xx (bad auth policy,
   * over-cap request, endpoint disabled) as Unknown.
   */
  verifyEmails: async (emails: string[]): Promise<EmailVerifyResult[]> => {
    if (emails.length === 0) return [];
    try {
      const res = await apiClient.post<{ results: EmailVerifyResult[] }>(
        '/integrations/email/verify',
        { emails },
      );
      return res.data?.results ?? [];
    } catch (err) {
      console.error('verifyEmails failed — degrading to Unknown for every input.', err);
      // Graceful-degrade: FE sees Unknown → Next stays enabled. Matches
      // the BE contract when the ZeroBounce client itself soft-disables.
      return emails.map((email) => ({
        email,
        status: 'Unknown',
        subStatus: 'http_error',
        didYouMean: null,
      }));
    }
  },

  insertCompound: async (
    templateId: string,
    stepId: string,
    sectionId: string,
    payload: {
      rows: {
        // `null` in a column slot means "leave this grid cell empty" — the
        // domain reserves the width but doesn't place a field. Lets a single
        // narrow input sit on a wider row (e.g. Postal Code on a 3-col grid).
        columns: ({
          type: WorkflowFieldType;
          labelEn: string;
          labelFr?: string | null;
          required?: boolean;
          properties?: WorkflowFieldPropertiesPayload;
        } | null)[];
      }[];
    },
  ): Promise<{ rows: FormRow[] }> => {
    const res = await apiClient.post<{ rows: FormRow[] }>(
      `/workflows/${templateId}/steps/${stepId}/sections/${sectionId}/compound`,
      payload,
    );
    return res.data;
  },

  // ----- Additional document entries (AdditionalDocuments step) -----

  addAdditionalDocumentEntry: async (
    templateId: string,
    stepId: string,
    body: { label: string },
  ): Promise<AdditionalDocumentEntry> => {
    const res = await apiClient.post<AdditionalDocumentEntry>(
      `/workflows/${templateId}/steps/${stepId}/additional-document-entries`,
      body,
    );
    return res.data;
  },

  editAdditionalDocumentEntry: async (
    templateId: string,
    stepId: string,
    entryId: string,
    body: {
      label: string;
      allowedExtensions: string[];
      isRequired: boolean;
      doNotParseToPdf: boolean;
      maxFileSizeMb: number;
    },
  ): Promise<AdditionalDocumentEntry> => {
    const res = await apiClient.patch<AdditionalDocumentEntry>(
      `/workflows/${templateId}/steps/${stepId}/additional-document-entries/${entryId}`,
      body,
    );
    return res.data;
  },

  deleteAdditionalDocumentEntry: async (
    templateId: string,
    stepId: string,
    entryId: string,
  ): Promise<void> => {
    await apiClient.delete(
      `/workflows/${templateId}/steps/${stepId}/additional-document-entries/${entryId}`,
    );
  },

  /**
   * Replace the gating conditions on an additional document entry. Same wire
   * shape as setFileEntryConditions — empty list + null combinator clears it.
   */
  setAdditionalDocumentEntryConditions: async (
    templateId: string,
    stepId: string,
    entryId: string,
    body: {
      conditions: FileEntryCondition[];
      combinator: 'all' | 'any' | null;
    },
  ): Promise<AdditionalDocumentEntry> => {
    const res = await apiClient.put<AdditionalDocumentEntry>(
      `/workflows/${templateId}/steps/${stepId}/additional-document-entries/${entryId}/conditions`,
      body,
    );
    return res.data;
  },

  /**
   * Replace the extension allow-list, combined-size cap, and/or section
   * visibility on the AdditionalDocuments step's unnamed "optional documents"
   * bucket — separate from the named entries. `maxFileSizeMb` and
   * `showSection` are optional; omit either to leave the persisted value
   * untouched.
   */
  setOptionalDocumentExtensions: async (
    templateId: string,
    stepId: string,
    extensions: string[],
    maxFileSizeMb?: number,
    showSection?: boolean,
  ): Promise<WorkflowStep> => {
    const res = await apiClient.put<WorkflowStep>(
      `/workflows/${templateId}/steps/${stepId}/optional-document-extensions`,
      { extensions, maxFileSizeMb, showSection },
    );
    return res.data;
  },

  // ----- OneSpanESign step config -----

  /**
   * Replace the OneSpan signing config on a OneSpanESign step. Pass `null` to
   * clear the config; pass a `OneSpanConfig` to overwrite it wholesale (drawer
   * authors everything in one form, so partial updates aren't supported).
   */
  setOneSpanConfig: async (
    templateId: string,
    stepId: string,
    config: OneSpanConfigPayload | null,
  ): Promise<WorkflowStep> => {
    const res = await apiClient.put<WorkflowStep>(
      `/workflows/${templateId}/steps/${stepId}/onespan-config`,
      config,
    );
    return res.data;
  },

  // ----- Welcome step config -----

  /**
   * Replace the welcome landing-card config on a Welcome step. Pass `null` to
   * clear back to runtime defaults; pass a full `WelcomeConfig` to overwrite it
   * wholesale (the modal authors everything in one form).
   */
  setWelcomeConfig: async (
    templateId: string,
    stepId: string,
    config: WelcomeConfig | null,
  ): Promise<WorkflowStep> => {
    const res = await apiClient.put<WorkflowStep>(
      `/workflows/${templateId}/steps/${stepId}/welcome-config`,
      config,
    );
    return res.data;
  },

  // ----- Editor lock -----

  /** Acquire (or refresh if already held by you) the editor lock. Resolves on 200/409. */
  acquireLock: async (templateId: string): Promise<WorkflowTemplate> => {
    const res = await apiClient.post<WorkflowTemplate>(`/workflows/${templateId}/lock`);
    return res.data;
  },

  /** Release the editor lock. Idempotent — releasing a lock you don't hold is a silent no-op. */
  releaseLock: async (templateId: string): Promise<void> => {
    await apiClient.delete(`/workflows/${templateId}/lock`);
  },

  /**
   * Owner-only: forcibly clear the editor lock when a colleague has acquired it and
   * gone idle. Used as a "take back my workflow" affordance from the details page.
   * BE returns 403 for non-owners.
   */
  forceReleaseLock: async (templateId: string): Promise<void> => {
    await apiClient.delete(`/workflows/${templateId}/lock/force`);
  },

  // ----- Upload URL minter -----

  createUploadUrl: async (
    templateId: string,
    body: { filename: string; contentType?: string; contentLength: number },
  ): Promise<{ uploadUrl: string; key: string; maxUploadBytes: number }> => {
    const res = await apiClient.post<{
      uploadUrl: string;
      key: string;
      maxUploadBytes: number;
    }>(`/workflows/${templateId}/upload-url`, body);
    // CloudFront's SPA fallback rewrites ANY 404 from the API origin into `200` +
    // index.html (web.yaml CustomErrorResponses is distribution-wide — it can't be
    // scoped to exclude /api/*). A 404 from this endpoint therefore arrives here as a
    // successful HTML response, and `uploadUrl` reads back `undefined`. Without this
    // guard the caller hands that straight to `axios.put`, which stringifies it to
    // "undefined", resolves it against the current page, and PUTs to
    // /originator/workflows/undefined — where CloudFront's default behavior (GET/HEAD/
    // OPTIONS only) answers 403, disguising an authorization 404 as a permissions error.
    // Fail loudly and honestly instead.
    if (!res.data?.uploadUrl || !res.data?.key) {
      throw new Error(
        'Upload URL request did not return a usable URL. The workflow may not exist or may not be yours to edit.',
      );
    }
    return res.data;
  },

  createDownloadUrl: async (templateId: string, key: string): Promise<string> => {
    const res = await apiClient.post<{ downloadUrl: string }>(
      `/workflows/${templateId}/download-url`,
      { key },
    );
    return res.data.downloadUrl;
  },

  // ----- Approver -----

  queue: async (): Promise<WorkflowTemplate[]> => {
    const res = await apiClient.get<WorkflowTemplate[]>('/workflows/queue');
    return res.data;
  },

  // Orphaned as of the Phase 9 audit work — the Audit History page now uses auditLog().
  // The backend /workflows/recent-decisions endpoint still exists; restore this (and the
  // WorkflowDecision type + RecentDecisions component) if a recent-decisions view returns.
  // recentDecisions: async (): Promise<WorkflowDecision[]> => {
  //   const res = await apiClient.get<WorkflowDecision[]>('/workflows/recent-decisions');
  //   return res.data;
  // },

  /**
   * Audit History feed. Search and date range are applied SERVER-side: the feed
   * is capped to the newest N rows, so filtering in the browser would only ever
   * search inside that window — a date range covering last month would come back
   * empty whenever the newest N rows are all recent.
   *
   * @param q    matches submitter name, email, or oid (case-insensitive substring)
   * @param from inclusive lower bound, ISO-8601
   * @param to   inclusive upper bound, ISO-8601 (the BE widens a midnight value to
   *             end-of-day so a single-day range selects that whole day)
   */
  auditLog: async (params?: {
    q?: string;
    from?: string;
    to?: string;
  }): Promise<AuditLogEntry[]> => {
    const res = await apiClient.get<AuditLogEntry[]>('/workflows/audit', {
      params: {
        ...(params?.q ? { q: params.q } : {}),
        ...(params?.from ? { from: params.from } : {}),
        ...(params?.to ? { to: params.to } : {}),
      },
    });
    return res.data;
  },

  approve: async (
    id: string,
    versionNumber: number,
    language?: string,
  ): Promise<WorkflowTemplate> => {
    // ?lang=<en|fr> drives the SES notification body's single-language render.
    // Omitted → the BE falls back to English. Kept optional so existing callers
    // (unit tests, non-i18n contexts) don't break.
    const qs = language ? `?lang=${encodeURIComponent(language)}` : '';
    const res = await apiClient.post<WorkflowTemplate>(
      `/workflows/${id}/versions/${versionNumber}/approve${qs}`,
    );
    return res.data;
  },

  reject: async (
    id: string,
    versionNumber: number,
    comment: string,
    language?: string,
  ): Promise<WorkflowTemplate> => {
    const qs = language ? `?lang=${encodeURIComponent(language)}` : '';
    const res = await apiClient.post<WorkflowTemplate>(
      `/workflows/${id}/versions/${versionNumber}/reject${qs}`,
      { comment },
    );
    return res.data;
  },
};
